/**
 * Failure-recovery copy for the check-in validation flow — PLATFORM DEFAULTS.
 *
 * Kept as data (not hardcoded in the component) so the program/pass config can
 * override the wording per program WITHOUT an app release. OS vendors move their
 * location/camera permission settings around between releases, so this copy goes
 * stale over time and must be editable. Backend (ENG-2205) exposes an editable
 * `recoveryInstructions` field on the program config, seeded from these defaults;
 * GpsBody renders whatever config resolves to and falls back to these when a
 * program hasn't overridden them.
 *
 * Per failure state:
 *   headline  — required
 *   body      — optional paragraph
 *   steps     — { ios, android } (RecoveryPanel shows an OS toggle) OR string[]
 *
 * Any string may contain tokens like {radius}, interpolated at render from the
 * live spec (so venue-specific values stay dynamic even though the copy is data).
 */
export const DEFAULT_RECOVERY_COPY = {
  denied: {
    headline: "We don't have permission to see your location",
    steps: {
      ios: [
        "Tap the ᴬA button in Safari's address bar.",
        'Tap Website Settings.',
        'Set Location to Allow, then come back and try again.'
      ],
      android: [
        'Tap the ⋮ menu → Settings → Site settings → Location.',
        'Find this site and choose Allow.',
        'Come back and tap Try again.'
      ]
    }
  },
  unavailable: {
    headline: "Your phone's location is switched off",
    steps: {
      ios: ['Open Settings → Privacy & Security → Location Services and switch it on, then return here.'],
      android: ['Swipe down from the top of your screen and tap the Location tile, then return here.']
    }
  },
  timeout: {
    headline: "We couldn't get a GPS fix",
    body: 'This happens with weak signal or indoors. Step outside or near a window and try again — or check in another way below.'
  },
  outside: {
    headline: "You're not close enough yet",
    body: 'You need to be {radius}. Move closer to the venue and try again.'
  }
}

const interpolate = (str, vars) =>
  typeof str === 'string'
    ? str.replace(/\{(\w+)\}/g, (match, key) => (vars[key] != null ? vars[key] : match))
    : str

/**
 * Resolve the copy for a failure `state`: the program `override` (from config)
 * wins over the platform default, then {token}s are filled from `vars`.
 * Returns null for states with no default (so callers can skip rendering).
 */
export function resolveRecoveryCopy(state, override = null, vars = {}) {
  const base = DEFAULT_RECOVERY_COPY[state]
  if (!base) return null
  const merged = { ...base, ...(override || {}) }
  const steps = Array.isArray(merged.steps)
    ? merged.steps.map(s => interpolate(s, vars))
    : merged.steps
      ? {
          ios: (merged.steps.ios || []).map(s => interpolate(s, vars)),
          android: (merged.steps.android || []).map(s => interpolate(s, vars))
        }
      : null
  return {
    headline: interpolate(merged.headline, vars),
    body: interpolate(merged.body, vars),
    steps
  }
}
